import React from "react"
import PropTypes from "prop-types"
import { getGuid } from "../../Code/Utilities"

// We deliberately avoid `window.paypal.Buttons.driver("react", { React, ReactDOM })`
// (which the previous version used) because that driver internally calls
// `ReactDOM.findDOMNode` and/or `ReactDOM.render` — both of which were
// removed in React 19. The result was the silent "Expected element to be
// passed to render iframe" error and no buttons rendered.
//
// Instead, this component owns a plain <div ref={...} /> and calls
// `paypal.Buttons(opts).render(divEl)` imperatively, which is the supported
// vanilla-JS API and doesn't depend on any removed ReactDOM internals.

export default class PayPalSDK extends React.Component {
  static propTypes = {
    clientId: PropTypes.string,
    addToDonationAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    donationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    shippingPreference: PropTypes.string,
    onSuccess: PropTypes.func,
    catchError: PropTypes.func,
    onError: PropTypes.func,
    createOrder: PropTypes.func,
    createSubscription: PropTypes.func,
    createBillingAgreement: PropTypes.func,
    onApprove: PropTypes.func,
    style: PropTypes.object,
    options: PropTypes.object,
    showMessages: PropTypes.bool,
    showButtons: PropTypes.bool,
    onButtonReady: PropTypes.func,
    onClick: PropTypes.func,
    onCancel: PropTypes.func,
    onInit: PropTypes.func,
  }

  static defaultProps = {
    style: {},
    options: { currency: "USD" },
    shippingPreference: "GET_FROM_FILE",
  }

  constructor(props) {
    super(props)
    this.containerRef = React.createRef()
    this.state = { sdkReady: false }
  }

  componentDidMount() {
    const { clientId, components, options, showMessages, showButtons } = this.props
    const scriptOptions = { ...(options || {}) }
    if (!scriptOptions.clientId && clientId) scriptOptions.clientId = clientId
    if (!scriptOptions.clientId) scriptOptions.clientId = "sb"
    if (!scriptOptions.components && components) scriptOptions.components = components
    if (!scriptOptions.components) scriptOptions.components = showMessages === false ? "" : "messages"
    if (showButtons && !scriptOptions.components.toLowerCase().includes("buttons")) {
      scriptOptions.components += ",buttons"
    }
    addPaypalSdk(scriptOptions, () => {
      this.setState({ sdkReady: true }, () => {
        this.renderButtons()
        if (this.props.onButtonReady) this.props.onButtonReady()
      })
    })
  }

  componentDidUpdate(prevProps) {
    // Re-render the PayPal buttons when relevant inputs change. We don't
    // diff createOrder/onApprove since those are closures recreated every
    // render — we always use the latest via `this.props` inside the closures
    // we pass to paypal.Buttons (see `renderButtons` below).
    if (
      this.state.sdkReady && (
        prevProps.amount !== this.props.amount ||
        prevProps.addToDonationAmount !== this.props.addToDonationAmount ||
        prevProps.showButtons !== this.props.showButtons ||
        prevProps.clientId !== this.props.clientId
      )
    ) {
      this.renderButtons()
    }
  }

  componentWillUnmount() {
    this.unmountButtons()
  }

  unmountButtons() {
    if (this.buttonsInstance && typeof this.buttonsInstance.close === "function") {
      try { this.buttonsInstance.close() } catch (e) { /* ignore */ }
    }
    this.buttonsInstance = null
    if (this.containerRef.current) {
      this.containerRef.current.innerHTML = ""
    }
  }

  renderButtons() {
    const el = this.containerRef.current
    if (!el) return
    if (!this.props.showButtons) return
    if (typeof window === "undefined" || !window.paypal || !window.paypal.Buttons) return

    // Clear any previously-rendered buttons.
    this.unmountButtons()

    const handlers = {
      style: this.props.style || {},
      onClick: this.props.onClick,
      onCancel: this.props.onCancel,
      onInit: this.props.onInit,
      onError: this.props.onError,
      onApprove: (data, actions) => this.onApprove(data, actions),
    }
    if (this.props.createSubscription) {
      handlers.createSubscription = this.props.createSubscription
    } else if (this.props.createBillingAgreement) {
      handlers.createBillingAgreement = this.props.createBillingAgreement
    } else {
      handlers.createOrder = (data, actions) =>
        this.props.createOrder
          ? this.props.createOrder(data, actions)
          : this.createOrder(data, actions)
    }

    try {
      this.buttonsInstance = window.paypal.Buttons(handlers)
      if (
        this.buttonsInstance.isEligible &&
        !this.buttonsInstance.isEligible()
      ) {
        return
      }
      const result = this.buttonsInstance.render(el)
      if (result && typeof result.catch === "function") {
        result.catch(err => {
          console.error("PayPal Buttons render rejected:", err)
        })
      }
    } catch (err) {
      console.error("PayPal Buttons setup failed:", err)
    }
  }

  createOrder(data, actions) {
    const {
      donationId, currency, options, amount, shippingPreference, addToDonationAmount,
    } = this.props
    if (parseFloat(addToDonationAmount) > 0) {
      return actions.order.create({
        purchase_units: [
          {
            reference_id: "r3e7gb",
            amount: {
              currency_code: "USD",
              value: parseFloat(addToDonationAmount).toFixed(2),
            },
          },
        ],
      })
    }
    return actions.order.create({
      purchase_units: [
        {
          reference_id: `${donationId || "00000"}  ${getGuid()}`,
          amount: {
            currency_code: currency || (options && options.currency) || "USD",
            value: (parseFloat(amount) || 0).toFixed(2),
          },
        },
      ],
      application_context: { shipping_preference: shippingPreference },
    })
  }

  onApprove(data, actions) {
    return actions.order
      .capture()
      .then(details => {
        if (this.props.onSuccess) return this.props.onSuccess(details, data)
      })
      .catch(err => {
        if (this.props.catchError) return this.props.catchError(err)
      })
  }

  render() {
    return <div ref={this.containerRef} />
  }
}

function addPaypalSdk(options, onSDKReady) {
  const queryParams = []
  Object.keys(options).forEach(k => {
    const name = k.split(/(?=[A-Z])/).join("-").toLowerCase()
    queryParams.push(`${name}=${options[k]}`)
  })

  const url = `https://www.paypal.com/sdk/js?${queryParams.join("&")}`
  const existing = document.querySelector(`script[src="${url}"]`)

  if (existing) {
    if (typeof window !== "undefined" && window.paypal && window.paypal.Buttons) {
      if (onSDKReady) setTimeout(onSDKReady, 0)
    } else if (onSDKReady) {
      existing.addEventListener("load", () => onSDKReady())
    }
    return
  }

  const script = document.createElement("script")
  script.type = "text/javascript"
  script.src = url
  script.async = true
  script.onload = () => {
    if (onSDKReady) onSDKReady()
  }
  script.onerror = () => {
    console.error("PayPal SDK could not be loaded from", url)
  }
  document.body.appendChild(script)
}
